import React, { useCallback, useEffect, useMemo } from "react";
import { BaseModal } from "@/react/components/editors/base/BaseModal";
import { BaseModalBody } from "@/react/components/editors/base/BaseModalBody";
import { Button } from "@/modern/components/ui/button";
import { Label } from "@/modern/components/ui/label";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { awardTagSchema, AwardTagFormData } from "@/react/schema/awardTag.schema";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { PERSISTENCE_STATE_KEYS } from "@/other/constants/PersistenceStateKeys";
import { SwivuCombo } from "@/react/components/SwivuCombo";
import { Info } from "lucide-react";

interface AwardTagEditorProps {
  record: {
    pointRecords?: any[];
    [key: string]: any;
  };
  onClose: () => void;
  editorInstance?: any;
  context?: any;
}

export const AwardTagEditor = ({ record, onClose, editorInstance }: AwardTagEditorProps) => {
  const { project, userPreferences } = useSwivuBryntumContext();
  const { pointRecords = [record] } = record;

  // Get existing tags from store
  const existingTagOptions = useMemo(() => {
    // @ts-expect-error - Bryntum store types
    const tags = project?.incentivePointTagsStore?.records || [];
    return tags.map((tag: any) => ({
      label: tag.name,
      value: tag.name
    }));
  }, [project]);

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    formState: { errors }
  } = useForm<AwardTagFormData>({
    resolver: zodResolver(awardTagSchema),
    defaultValues: {
      tagName: ""
    }
  });

  // Load last used tag from board preferences
  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lastTagIdPref = project?.boardPreferencesStore?.find(
      (r: any) => r.key === PERSISTENCE_STATE_KEYS.lastAwardTagId
    ) as any;
    if (lastTagIdPref && lastTagIdPref.value) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const lastTag = (project as any).incentivePointTagsStore.getById(lastTagIdPref.value);
      if (lastTag) {
        setValue("tagName", lastTag.name);
      }
    }
  }, [project, setValue]);

  const onSave = async (data: AwardTagFormData) => {
    const tagName = data.tagName.trim();

    if (!tagName) {
      setError("tagName", { message: "Please enter a tag name" });
      return;
    }

    // @ts-expect-error - Bryntum store types
    const incentivePointTagsStore = project.incentivePointTagsStore;

    // Find or create the tag
    let tagRecord = incentivePointTagsStore.find((tag: any) => tag.name === tagName);

    if (!tagRecord) {
      // Create new tag
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      tagRecord = incentivePointTagsStore.add({
        name: tagName,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        boardId: (project as any).boardStore.first?.id
      })[0];

      // Wait for sync to get the ID from backend
      await project.commitAsync();
    }

    // Update board preferences with last used tag
    if (tagRecord.id && userPreferences) {
      userPreferences.updateSetting(PERSISTENCE_STATE_KEYS.lastAwardTagId, String(tagRecord.id), true);
    }

    // Update all point records with the tag
    pointRecords.forEach((pointRecord: any) => {
      pointRecord.tagId = tagRecord.id;
    });

    onClose();
  };

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // Get the current month for placeholder
  const currentMonth = useMemo(() => {
    return new Date().toLocaleString("default", { month: "long" });
  }, []);

  const footerRenderer = () => (
    <div className="border-t border-border bg-card w-full">
      <div className="flex items-center justify-end gap-2 p-3">
        <Button variant="outline" onClick={handleCancel} className="gap-2">
          <i className="fa-light fa-close" />
          Cancel
        </Button>
        <Button
          onClick={handleSubmit(onSave)}
          variant="outline"
          className="gap-2 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
        >
          <i className="fa-light fa-tag" />
          Use Tag
        </Button>
      </div>
    </div>
  );

  return (
    <BaseModal
      record={{ isPhantom: false, archived: false }}
      onClose={onClose}
      title="Add a Points Tag"
      description=""
      editorInstance={editorInstance}
      hideMenu={true}
      size={{
        width: "40em",
        height: "26em",
        minWidth: "35em",
        minHeight: "26em",
        maxWidth: "100vw",
        maxHeight: "90vh"
      }}
      className="!resize-none"
      modal={true}
      version={undefined}
      swivuAssistanceConfig={undefined}
      headerIconCls=""
      footerRenderer={footerRenderer}
      footerClassName="!p-0"
    >
      <BaseModalBody className="!p-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <Label htmlFor="tagName">Points Tag</Label>
          <Controller
            name="tagName"
            control={control}
            render={({ field }) => {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const SwivuComboAny = SwivuCombo as any;
              return (
                <SwivuComboAny
                  value={field.value}
                  onChange={(value: string) => {
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  name={field.name}
                  options={existingTagOptions}
                  placeholder={`Example: ${currentMonth} Points`}
                  isCombo={true}
                  createOnUnmatched={(value: string) => {
                    field.onChange(value);
                  }}
                  resetOnCreateOnUnmatched={false}
                  error={errors.tagName?.message}
                  inputElementClassName="flex w-full rounded-md text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-base border border-input bg-background h-swivu-input px-3 py-2"
                  menuMaxHeight="max-h-48"
                />
              );
            }}
          />
          <div className="text-xs text-muted-foreground">The last tag you used will appear automatically. Rename if needed.</div>
        </div>

        <div className="flex items-start gap-3 rounded-lg border border-border/60 bg-muted/30 p-4">
          <Info className="size-5 text-muted-foreground/70 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-muted-foreground leading-relaxed">
            You're awarding points. Give this batch a short, clear tag so you can find it easily later. Use 1–3 words
            that describe the timeframe or reason for the award (for example: <span className="font-medium text-foreground">'August Points'</span>, <span className="font-medium text-foreground">'Safety Week'</span>, or <span className="font-medium text-foreground">'Q4 Bonus'</span>).
          </p>
        </div>
      </BaseModalBody>
    </BaseModal>
  );
};
